import React, { useState } from "react";
import {
  MapPinned,
  Hotel,
  Car,
  UserCheck,
  FileCheck2,
  PartyPopper,
  Mountain,
  Camera,
  Utensils,
  Plane,
  ArrowRight,
} from "lucide-react";
import PageLayout from "@/components/layout/PageLayout";
import PageHero from "@/components/PageHero";
import InquiryDialog from "@/components/InquiryDialog";
import Seo from "@/components/Seo";
import { useSiteContent } from "@/context/SiteContentContext";

const SERVICES = [
  {
    icon: MapPinned,
    title: "Custom Itinerary Design",
    text: "Bespoke day-by-day journeys built around your interests, pace and budget. Free 60-min consultation.",
  },
  {
    icon: Hotel,
    title: "Hotel Curation",
    text: "From heritage palaces to family-run havelis — we book at insider rates with rooms we have inspected ourselves.",
  },
  {
    icon: Car,
    title: "Private Transport",
    text: "Air-conditioned sedans, SUVs and tempo travellers with English-speaking chauffeurs across India.",
  },
  {
    icon: UserCheck,
    title: "Expert Guides",
    text: "Government-licensed, multi-lingual guides in 14 cities — historians, naturalists and culture experts.",
  },
  {
    icon: FileCheck2,
    title: "Visa & Documentation",
    text: "End-to-end India e-visa assistance, FRRO support and travel insurance partnerships.",
  },
  {
    icon: PartyPopper,
    title: "Festival Specials",
    text: "Diwali, Holi, Pushkar Camel Fair, Kumbh Mela — we know exactly where to be, and when.",
  },
  {
    icon: Mountain,
    title: "Wildlife Safaris",
    text: "Tiger reserves, bird sanctuaries, jungle lodges. Premium permits & naturalist-led drives.",
  },
  {
    icon: Camera,
    title: "Photo Tours",
    text: "Routes and timings designed for photographers — golden hour at the Taj, festival portraits, wildlife.",
  },
  {
    icon: Utensils,
    title: "Culinary Journeys",
    text: "Home dining with families, market walks, cooking classes, spice trails and street-food crawls.",
  },
  {
    icon: Plane,
    title: "International Extensions",
    text: "Add Nepal, Dubai or Thailand to your India tour — booked as one seamless trip.",
  },
];

export default function Services() {
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const c = useSiteContent("services");

  return (
    <PageLayout>
      <Seo title="Our Services" description={c.hero_subtitle} />
      <PageHero
        eyebrow={c.hero_eyebrow}
        title={c.hero_title}
        subtitle={c.hero_subtitle}
        image={c.hero_image}
      />

      <section className="py-24 md:py-32" data-testid="services-grid">
        <div className="container-tti">
          <div className="grid lg:grid-cols-12 gap-12 mb-16">
            <div className="lg:col-span-5">
              <div className="eyebrow mb-4">Our Services</div>
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.05]">
                A full-service travel
                <br />
                <span className="italic text-deepred">atelier.</span>
              </h2>
            </div>
            <div className="lg:col-span-7 flex items-end">
              <p className="text-foreground/75 text-lg font-light leading-relaxed">
                Whether you're a first-time traveler with two free weeks or a seasoned India hand returning for your
                fifth trip, here is the menu of what we craft.
              </p>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-cream-200 border border-cream-200">
            {SERVICES.map((s) => (
              <div
                key={s.title}
                className="bg-cream p-8 md:p-10 hover:bg-white transition-colors group"
                data-testid={`service-${s.title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="w-12 h-12 rounded-sm bg-saffron-50 text-saffron flex items-center justify-center mb-6 group-hover:bg-saffron group-hover:text-white transition-colors">
                  <s.icon className="w-5 h-5" />
                </div>
                <h3 className="font-serif text-2xl mb-3">{s.title}</h3>
                <p className="text-sm text-foreground/70 font-light leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-cream-100" data-testid="services-process">
        <div className="container-tti">
          <div className="text-center mb-16">
            <div className="eyebrow mb-4">How we work</div>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight">From hello to namaste.</h2>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { num: "01", title: "Inquiry", text: "Drop us your dreams, dates, party size." },
              { num: "02", title: "Consultation", text: "60-min video call to understand your style." },
              { num: "03", title: "Curation", text: "We design a sample itinerary within 48 hrs." },
              { num: "04", title: "Refine & Confirm", text: "We iterate, you confirm, we lock in." },
            ].map((p) => (
              <div key={p.num} className="bg-white border border-cream-200 p-8">
                <div className="font-serif text-5xl text-saffron mb-4">{p.num}</div>
                <h3 className="font-serif text-2xl mb-2">{p.title}</h3>
                <p className="text-sm text-foreground/70 font-light">{p.text}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <button onClick={() => setInquiryOpen(true)} className="btn-primary" data-testid="services-cta">
              Start the conversation <ArrowRight className="w-4 h-4 ml-2" />
            </button>
          </div>
        </div>
      </section>

      <InquiryDialog open={inquiryOpen} onOpenChange={setInquiryOpen} />
    </PageLayout>
  );
}
